import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BdCompanyContextService,
  BdShareablesService,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  ShareableFolderObj,
  ShareableType,
  SHAREABLE_TYPE_DISPLAY_NAME,
} from '@bindoc-projects/resource-domain-lib';
import { MenuItem } from 'primeng/api';
import { combineLatest, forkJoin, skip, take } from 'rxjs';

import { BdFolderTableComponent } from '../folder-table/folder-table.component';
import { FolderItem } from '../shareables-overview.models';

@Component({
  selector: 'bd-recycle-bin-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <bd-folder-table
      [items]="items()"
      [loading]="loading()"
      [showType]="showType()"
      [typeFilter]="true"
      [sortMode]="'client'"
      emptyMessage="Papierkorb ist leer"
      [menuItemsProvider]="menuItemsProvider"
    ></bd-folder-table>
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
      }
    `,
  ],
  imports: [BdFolderTableComponent],
})
export class BdRecycleBinTabComponent implements OnInit {
  public readonly shareableType = input<ShareableType | undefined>(undefined);
  /** Show a "Typ" column — useful when more than one shareable type is listed. */
  public readonly showType = input(false);

  public readonly items = signal<FolderItem[]>([]);
  public readonly loading = signal(false);

  private readonly _resourcesService = inject(BdShareablesService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _destroyRef = inject(DestroyRef);

  public readonly menuItemsProvider = (item: FolderItem): MenuItem[] => [
    {
      label: 'Wiederherstellen',
      icon: 'pi pi-refresh',
      command: () => this._restore(item),
    },
  ];

  ngOnInit(): void {
    this.reload();
    this._companyContext.selectedCompanyId$
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.reload());
  }

  /**
   * Loads the bins that belong to the selected scope.
   *
   * The personal bin is *not* part of a company. With a company selected, only
   * that company's groups' bins are fetched — otherwise deleting something of
   * your own would surface it inside every company you switch to, which is not
   * what the scope says it is showing. Without a selection the tab is the
   * user's own overview again: their bin plus every group they can reach.
   *
   * The id is read alongside the scopes (not before them) so a switch cannot
   * pair a fresh id with the previous selection's groups.
   */
  public reload(): void {
    this.loading.set(true);
    combineLatest([
      this._companyContext.selectedCompanyId$,
      this._companyContext.selectedCompanyScopes$,
    ])
      .pipe(take(1))
      .subscribe(([selectedCompanyId, scopes]) => {
        const groupRequests = Object.fromEntries(
          scopes.flatMap((s) =>
            s.groups.map((g) => [
              `group_${g.id}`,
              this._resourcesService.groupRecycleBin(g.id),
            ]),
          ),
        ) as Record<string, ReturnType<BdShareablesService['recycleBin']>>;

        const requests: Record<
          string,
          ReturnType<BdShareablesService['recycleBin']>
        > = selectedCompanyId
          ? groupRequests
          : { user: this._resourcesService.recycleBin(), ...groupRequests };

        // Reachable now for a company whose groups have no bins of their own —
        // an empty list is the right answer there, not a personal bin.
        if (Object.keys(requests).length === 0) {
          this.items.set([]);
          this.loading.set(false);
          return;
        }

        forkJoin(requests).subscribe({
          next: (results) => {
            const all: FolderItem[] = [];
            for (const folder of Object.values(
              results,
            ) as ShareableFolderObj[]) {
              for (const child of folder.children) {
                if (
                  this.shareableType() &&
                  child.shareableType !== this.shareableType()
                ) {
                  continue;
                }
                all.push({
                  id: child.shareableId,
                  type: child.shareableType,
                  isFolder: child.isFolder,
                  name: child.name,
                  typeLabel:
                    SHAREABLE_TYPE_DISPLAY_NAME[child.shareableType] ??
                    child.shareableType,
                  modifiedDate: child.modifiedDate,
                  isRecycleBinItem: true,
                  parentFolderId: child.parentFolderId,
                  labels: child.labels,
                  allowedPermissions: child.allowedShareablePermissions,
                });
              }
            }
            this.items.set(all);
            this.loading.set(false);
          },
          error: () => {
            this.items.set([]);
            this.loading.set(false);
          },
        });
      });
  }

  private _restore(item: FolderItem): void {
    this._resourcesService
      .restoreShareable(item.type, item.id)
      .pipe(take(1))
      .subscribe(() => {
        this.items.update((current) => current.filter((i) => i.id !== item.id));
      });
  }
}
